/* JCI EDITS - /pages/product-result (templates/page.ymm-custom.liquid).
 *
 * Turns the YMM app's client-rendered result grid into the same product
 * listing used on /collections/all.
 *
 * WHY AN ADAPTER IS NEEDED
 * The YMM app writes its grid into #products from JavaScript (its own
 * markup says "PRODUCTS ARE WRITTEN IN THIS PART DYNAMICALLY"), so this
 * page has no Liquid `product` objects and, being page_type "page" with
 * no collection, no native Shopify facets either. Everything server-
 * rendered on /collections/all therefore has to be re-obtained here.
 *
 * HOW THE CARDS STAY IDENTICAL
 * Cards are NOT rebuilt in JS. For each YMM result we fetch
 * /products/{handle}?view=jci-card, a layout-less template that renders
 * the same 'product-item' snippet with the same jci_media_preview flag
 * that /collections/all uses, and inject that markup. So the card
 * implementation, its Loox metafield ratings, fitment tags, price/"From"
 * logic and capped media set all come from Liquid, exactly once.
 *
 * Once injected we dispatch the theme's own `shopify:section:load` event
 * so jci-product-listing.js initialises over the new markup - that gives
 * us its media switching, swipe, hover-scrub and grid switching for free
 * rather than reimplementing any of it.
 *
 * FILTER/SORT ARCHITECTURE
 * The YMM `rq` query is the source of truth for fitment and is never
 * touched: we only ever filter/sort the set it already returned, in
 * memory. Type/sort state lives in its own `jci_type` / `jci_sort`
 * params which are appended alongside `rq`, so deep links work and the
 * vehicle result can't be reset or bypassed by a merchandising filter.
 */
(() => {
  const productContainer = document.getElementById('product_container');
  const appGrid = document.getElementById('products');
  const sectionHeader = document.querySelector('.section-header');
  if (!productContainer || !appGrid || !sectionHeader) return;

  const TYPE_PARAM = 'jci_type';
  const SORT_PARAM = 'jci_sort';

  const SORT_OPTIONS = [
    { value: 'manual', label: 'Featured' },
    { value: 'title-ascending', label: 'Alphabetically, A-Z' },
    { value: 'title-descending', label: 'Alphabetically, Z-A' },
    { value: 'price-ascending', label: 'Price, low to high' },
    { value: 'price-descending', label: 'Price, high to low' },
    { value: 'created-descending', label: 'Date, new to old' },
    { value: 'created-ascending', label: 'Date, old to new' }
  ];

  const readParam = (name) => new URLSearchParams(window.location.search).get(name) || '';

  const VEHICLE_UPPERCASE_WORDS = new Set(['bmw', 'gmc', 'ram', 'mini', 'vw']);

  const humanizeVehicleValue = (value) => String(value || '')
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      const normalized = word.toLowerCase();
      if (VEHICLE_UPPERCASE_WORDS.has(normalized) || (/\d/.test(word) && /[a-z]/i.test(word))) {
        return word.toUpperCase();
      }
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    })
    .join(' ');

  const readVehicleTitle = () => {
    const values = {};
    readParam('rq').split('__').forEach((part) => {
      const match = part.match(/^(fd|mk|md)_(.+)/);
      if (match) values[match[1]] = humanizeVehicleValue(match[2]);
    });
    return [values.fd, values.mk, values.md].filter(Boolean).join(' ');
  };

  /* Keeps rq (and anything else the YMM app put on the URL) untouched -
     only our own key is added/removed. replaceState so the browser's
     back button still steps through vehicle searches, not filter tweaks. */
  const writeParam = (name, value) => {
    const url = new URL(window.location.href);
    if (value) url.searchParams.set(name, value);
    else url.searchParams.delete(name);
    window.history.replaceState({}, '', url);
  };

  let state = { type: readParam(TYPE_PARAM), sort: readParam(SORT_PARAM) || 'manual' };
  let products = [];

  /* ---- page shell: mirror /collections/all's container + facet ---------
     jci-product-listing.css is written against
     .jci-product-listing__container > .jci-product-facet, and
     jci-product-listing.js binds to [data-jci-product-listing], so the
     app's result container is kept intact as the source of truth inside
     that structure. A separate presentation grid receives the redesigned
     cards, so this adapter never clears or replaces DOM owned by the app. */
  const container = document.createElement('div');
  container.className = 'container jci-product-listing__container';

  const facet = document.createElement('product-facet');
  facet.className = 'product-facet jci-product-facet is-filter-closed';
  facet.setAttribute('data-jci-product-listing', '');
  facet.setAttribute(
    'data-jci-listing-theme',
    document.body.classList.contains('jci-product-listing-theme--light') ? 'light' : 'dark'
  );
  facet.setAttribute('data-jci-grid', '4');

  const main = document.createElement('div');
  main.className = 'product-facet__main anchor';

  productContainer.parentNode.insertBefore(container, productContainer);
  container.appendChild(facet);
  facet.appendChild(main);

  const toolbar = document.createElement('div');
  toolbar.className = 'product-facet__meta-bar anchor jci-product-query-bar';
  main.appendChild(toolbar);
  main.appendChild(productContainer);

  // Keep #products available to the YMM app and observe it as the canonical
  // result set. The shopper sees this sibling grid, which uses the same
  // markup/classes as /collections/all without taking ownership of app DOM.
  appGrid.setAttribute('data-jci-ymm-source', '');
  appGrid.setAttribute('aria-hidden', 'true');

  const grid = document.createElement('div');
  grid.className = 'grid product-list__inner';
  grid.setAttribute('data-jci-ymm-grid', '');
  appGrid.parentNode.insertBefore(grid, appGrid.nextSibling);

  /* ---- header: /collections/all's page-header + stats ------------------ */
  sectionHeader.classList.add('jci-ymm-header');
  const syncVehicleTitle = () => {
    const titleEl = sectionHeader.querySelector('.ymm_pg_title');
    if (!titleEl) return;
    const vehicleTitle = readVehicleTitle();
    const desiredTitle = vehicleTitle || 'Products';
    if (titleEl.textContent.trim() !== desiredTitle) titleEl.textContent = desiredTitle;
  };
  syncVehicleTitle();
  if (typeof MutationObserver !== 'undefined') {
    const titleObserver = new MutationObserver(syncVehicleTitle);
    titleObserver.observe(sectionHeader, { childList: true, characterData: true, subtree: true });
  }
  const countWrap = sectionHeader.querySelector('h4');
  const countEl = sectionHeader.querySelector('#total_products');

  const syncCount = () => {
    if (!countEl || !countWrap) return;
    const match = (countEl.textContent || '').match(/\d+/);
    const total = match ? match[0] : String(products.length || 0);
    countWrap.classList.add('jci-page-stats');
    countEl.classList.add('jci-page-stats__item');
    countEl.innerHTML = '';
    const b = document.createElement('b');
    b.textContent = total;
    const label = document.createElement('span');
    label.textContent = 'Products';
    countEl.appendChild(b);
    countEl.appendChild(label);
  };

  /* ---- toolbar controls -------------------------------------------------
     Built from .jci-product-query-control - the same details/summary
     dropdown /collections/all uses for its desktop Make/Model/Type
     facets - so these inherit that styling instead of introducing a new
     control design. Make and Model are deliberately not offered: the YMM
     selector above already fixes both, so repeating them here would only
     let a shopper contradict their own vehicle. */
  const buildControl = ({ key, label, options, selected, onSelect }) => {
    const details = document.createElement('details');
    details.className = 'jci-product-query-control';
    details.setAttribute('data-jci-query-control', '');
    details.dataset.jciYmmControl = key;

    const summary = document.createElement('summary');
    summary.className = 'jci-product-query-control__trigger';
    const labelEl = document.createElement('span');
    labelEl.className = 'jci-product-query-control__label';
    labelEl.textContent = label;
    const valueEl = document.createElement('span');
    valueEl.className = 'jci-product-query-control__value';
    valueEl.textContent = (options.find((o) => o.value === selected) || options[0]).label;
    summary.appendChild(labelEl);
    summary.appendChild(valueEl);
    summary.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 7 5 6 5-6" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>');

    const panel = document.createElement('div');
    panel.className = 'jci-product-query-control__panel';
    const list = document.createElement('div');
    list.className = 'jci-product-query-control__options';
    list.setAttribute('role', 'list');

    options.forEach((option) => {
      const item = document.createElement('a');
      item.className = 'jci-product-query-option';
      item.setAttribute('role', 'listitem');
      item.href = '#';
      item.dataset.jciQueryOptionName = option.label.toLowerCase();
      if (option.value === selected) item.classList.add('is-active');

      const check = document.createElement('span');
      check.className = 'jci-product-query-option__check';
      const text = document.createElement('span');
      text.className = 'jci-product-query-option__label';
      text.textContent = option.label;
      item.appendChild(check);
      item.appendChild(text);

      if (option.count != null) {
        const count = document.createElement('span');
        count.className = 'jci-product-query-option__count';
        count.textContent = option.count;
        item.appendChild(count);
      }

      item.addEventListener('click', (event) => {
        event.preventDefault();
        details.removeAttribute('open');
        onSelect(option.value);
      });
      list.appendChild(item);
    });

    panel.appendChild(list);
    details.appendChild(summary);
    details.appendChild(panel);
    return details;
  };

  const toolbarRight = document.createElement('div');
  toolbarRight.className = 'jci-product-toolbar__right';
  toolbar.appendChild(toolbarRight);

  /* Mobile uses the same Filters trigger as /collections/all. Type and
     Sort By live together in the shared drawer below; desktop keeps the
     two inline query controls built further down. */
  const filterTriggerTemplate = document.createElement('template');
  filterTriggerTemplate.innerHTML = `
    <button type="button" is="toggle-button" class="jci-product-sort-button jci-product-filter-button hidden-lap-and-up" aria-expanded="false" aria-controls="jci-mobile-filters">
      <span class="jci-icon-wrap">
        <svg class="jci-product-filter-icon jci-product-filter-icon--crisp" width="16" height="13" viewBox="0 0 32 26" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M1 5h17M26 5h5M1 13h5M14 13h17M1 21h17M26 21h5"/><circle cx="22" cy="5" r="4"/><circle cx="10" cy="13" r="4"/><circle cx="22" cy="21" r="4"/></svg>
        <svg class="jci-product-filter-icon" width="16" height="13" viewBox="0 0 512 410.03" fill="currentColor" aria-hidden="true"><path d="M16,82.63h260.51c7.19,29.03,33.45,50.63,64.67,50.63s57.49-21.59,64.67-50.63h90.14c8.84,0,16-7.16,16-16s-7.16-16-16-16h-90.14C398.67,21.59,372.41,0,341.18,0s-57.49,21.59-64.67,50.63H16c-8.84,0-16,7.16-16,16s7.16,16,16,16M341.19,32c19.09,0,34.63,15.53,34.63,34.63s-15.53,34.63-34.63,34.63-34.63-15.53-34.63-34.63,15.53-34.63,34.63-34.63M16,221.02h90.14c7.19,29.03,33.45,50.63,64.67,50.63s57.49-21.59,64.67-50.63h260.51c8.84,0,16-7.16,16-16s-7.16-16-16-16H235.49c-7.19-29.03-33.45-50.63-64.67-50.63s-57.49,21.59-64.67,50.63H16c-8.84,0-16,7.16,16,16M170.81,170.39c19.09,0,34.63,15.53,34.63,34.63s-15.53,34.63-34.63,34.63-34.63-15.53-34.63-34.63,15.53-34.63,34.63-34.63M496,327.4h-90.14c-7.19-29.03-33.45-50.63-64.67-50.63s-57.49,21.59-64.67,50.63H16c-8.84,0-16,7.16,16,16h260.51c7.19,29.03,33.45,50.63,64.67,50.63s57.49-21.59,64.67-50.63h90.14c8.84,0,16-7.16,16-16s-7.16-16-16-16M341.19,378.03c-19.09,0-34.63-15.53-34.63-34.63s15.53-34.63,34.63-34.63,34.63,15.53,34.63,34.63-15.53,34.63-34.63,34.63"/></svg>
        <span class="product-facet__active-count bubble-count" data-jci-ymm-toolbar-badge hidden></span>
      </span>
      <span class="jci-product-sort-button__label">Filters</span>
    </button>`;
  const filterButton = filterTriggerTemplate.content.firstElementChild;
  toolbarRight.appendChild(filterButton);

  const mobileDrawer = document.querySelector('[data-jci-ymm-mobile-filters]');
  const mobileTypeList = mobileDrawer?.querySelector('[data-jci-ymm-mf-list="type"]');
  const mobileSortList = mobileDrawer?.querySelector('[data-jci-ymm-mf-list="sort"]');
  const mobileApply = mobileDrawer?.querySelector('[data-jci-ymm-mf-apply]');
  const mobileClear = mobileDrawer?.querySelector('[data-jci-ymm-mf-clear]');
  const mobileToolbarBadge = filterButton.querySelector('[data-jci-ymm-toolbar-badge]');

  const selectMobileTab = (key) => {
    if (!mobileDrawer) return;
    mobileDrawer.querySelectorAll('[data-jci-ymm-mf-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.jciYmmMfTab === key));
    });
    mobileDrawer.querySelectorAll('[data-jci-ymm-mf-pane]').forEach((pane) => {
      pane.hidden = pane.dataset.jciYmmMfPane !== key;
    });
  };

  const buildMobileRows = (list, options, key, selected) => {
    if (!list) return;
    list.textContent = '';
    options.forEach((option) => {
      const row = document.createElement('label');
      row.className = 'jci-mobile-filters__opt';
      row.dataset.jciYmmMfName = option.label.toLowerCase();

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `jci-ymm-${key}`;
      input.value = option.value;
      input.dataset.jciYmmMfInput = key;
      input.checked = option.value === selected;

      const box = document.createElement('span');
      box.className = 'jci-mobile-filters__opt-box';
      const label = document.createElement('span');
      label.className = 'jci-mobile-filters__opt-label';
      label.textContent = option.label;

      row.appendChild(input);
      row.appendChild(box);
      row.appendChild(label);
      if (option.count != null) {
        const count = document.createElement('span');
        count.className = 'jci-mobile-filters__opt-count';
        count.textContent = option.count;
        row.appendChild(count);
      }
      list.appendChild(row);
    });
  };

  const refreshMobileBadges = () => {
    if (!mobileDrawer) return;
    const typeValue = mobileDrawer.querySelector('[data-jci-ymm-mf-input="type"]:checked')?.value || '';
    const sortValue = mobileDrawer.querySelector('[data-jci-ymm-mf-input="sort"]:checked')?.value || 'manual';
    const typeCount = typeValue ? 1 : 0;
    const sortCount = sortValue !== 'manual' ? 1 : 0;
    const total = typeCount + sortCount;

    [['type', typeCount], ['sort', sortCount]].forEach(([key, count]) => {
      const badge = mobileDrawer.querySelector(`[data-jci-ymm-mf-badge="${key}"]`);
      if (!badge) return;
      badge.hidden = count === 0;
      badge.textContent = count;
    });
    mobileToolbarBadge.hidden = total === 0;
    mobileToolbarBadge.textContent = total;
    filterButton.classList.toggle('is-set', total > 0);
    if (mobileClear) mobileClear.hidden = total === 0;
    if (mobileApply) mobileApply.textContent = total ? `Show results (${total})` : 'Show results';
  };

  const syncMobileDrawer = (typeOptions) => {
    if (!mobileDrawer) return;
    buildMobileRows(mobileTypeList, typeOptions, 'type', state.type);
    buildMobileRows(mobileSortList, SORT_OPTIONS, 'sort', state.sort);
    refreshMobileBadges();
  };

  if (mobileDrawer) {
    mobileDrawer.querySelectorAll('[data-jci-ymm-mf-tab]').forEach((tab) => {
      tab.addEventListener('click', () => selectMobileTab(tab.dataset.jciYmmMfTab));
    });
    mobileDrawer.addEventListener('change', (event) => {
      if (event.target.matches('[data-jci-ymm-mf-input]')) refreshMobileBadges();
    });
    mobileApply?.addEventListener('click', () => {
      state.type = mobileDrawer.querySelector('[data-jci-ymm-mf-input="type"]:checked')?.value || '';
      state.sort = mobileDrawer.querySelector('[data-jci-ymm-mf-input="sort"]:checked')?.value || 'manual';
      writeParam(TYPE_PARAM, state.type);
      writeParam(SORT_PARAM, state.sort === 'manual' ? '' : state.sort);
      mobileDrawer.open = false;
      apply();
    });
    mobileClear?.addEventListener('click', () => {
      state.type = '';
      state.sort = 'manual';
      writeParam(TYPE_PARAM, '');
      writeParam(SORT_PARAM, '');
      mobileDrawer.open = false;
      apply();
    });
  }

  const buildGridIcon = (cols, mobile) => {
    const cells = mobile ? (cols === 1 ? 2 : 4) : cols * cols;
    let html = `<span class="jci-product-grid-icon jci-product-grid-icon--${cols}" aria-hidden="true">`;
    for (let i = 0; i < cells; i += 1) html += '<i></i>';
    return `${html}</span>`;
  };

  /* Same three desktop choices as /collections/all: 3-up, 4-up, list.
     The list icon is the same client SVG that snippet uses. */
  const LIST_ICON = '<span class="jci-product-grid-icon jci-product-grid-icon--list" aria-hidden="true"><svg viewBox="0 0 512 512" fill="currentColor"><path d="M98.33,98.8H15.05v-73.2h83.28v73.2ZM497,98.8H164.33v-73.2h332.67v73.2ZM98.33,228H15.05v-73.2h83.28v73.2ZM497,228H164.33v-73.2h332.67v73.2ZM98.33,357.2H15.05v-73.2h83.28v73.2ZM497,357.2H164.33v-73.2h332.67v73.2ZM98.33,486.4H15.05v-73.2h83.28v73.2ZM497,486.4H164.33v-73.2h332.67v73.2Z"/></svg></span>';

  const gridSwitcher = document.createElement('div');
  gridSwitcher.className = 'jci-product-grid-switcher hidden-pocket';
  gridSwitcher.setAttribute('role', 'group');
  gridSwitcher.setAttribute('aria-label', 'Product grid columns');
  gridSwitcher.innerHTML = [3, 4]
    .map((cols) => `<button type="button" data-jci-grid-choice="${cols}" aria-label="Show ${cols} product columns" aria-pressed="${cols === 4}">${buildGridIcon(cols)}</button>`)
    .join('')
    + `<button type="button" data-jci-grid-choice="list" aria-label="Show list view" aria-pressed="false">${LIST_ICON}</button>`;

  /* List view: same .jci-product-table markup jci-product-list-view.liquid
     builds, so [data-jci-grid="list"] in jci-product-listing.css swaps it
     in and hides the grid with no extra logic here. Rows come from the
     same per-product fetch as the cards. */
  const tableWrap = document.createElement('div');
  tableWrap.className = 'jci-product-table-wrap';
  tableWrap.setAttribute('data-jci-product-table', '');
  tableWrap.innerHTML =
    '<div class="jci-product-table-scroll"><table class="jci-product-table"><thead><tr>'
    + '<th class="jci-table-cell jci-table-cell--index" scope="col">#</th>'
    + '<th class="jci-table-cell jci-table-cell--product" scope="col">Product</th>'
    + '<th class="jci-table-cell jci-table-cell--type" scope="col">Type</th>'
    + '<th class="jci-table-cell jci-table-cell--fitment" scope="col">Fitment</th>'
    + '<th class="jci-table-cell jci-table-cell--rating" scope="col">Rating</th>'
    + '<th class="jci-table-cell jci-table-cell--price" scope="col">Price</th>'
    + '</tr></thead><tbody></tbody></table></div>';
  const tableBody = tableWrap.querySelector('tbody');
  grid.parentNode.insertBefore(tableWrap, grid.nextSibling);

  const mobileGridSwitcher = document.createElement('div');
  mobileGridSwitcher.className = 'jci-product-grid-switcher jci-product-grid-switcher--mobile hidden-lap-and-up';
  mobileGridSwitcher.setAttribute('role', 'group');
  mobileGridSwitcher.setAttribute('aria-label', 'Product grid columns on mobile');
  mobileGridSwitcher.innerHTML = [1, 2]
    .map((cols) => `<button type="button" data-jci-mobile-grid-choice="${cols}" aria-label="Show ${cols} product column${cols > 1 ? 's' : ''}" aria-pressed="${cols === 2}">${buildGridIcon(cols, true)}</button>`)
    .join('');

  let typeControl = null;
  let sortControl = null;

  const renderControls = () => {
    const counts = new Map();
    products.forEach((product) => {
      if (!product.type) return;
      counts.set(product.type, (counts.get(product.type) || 0) + 1);
    });
    const typeOptions = [{ value: '', label: 'All', count: products.length }].concat(
      Array.from(counts.keys()).sort().map((type) => ({ value: type, label: type, count: counts.get(type) }))
    );

    const nextType = buildControl({
      key: 'type',
      label: 'Type',
      options: typeOptions,
      selected: state.type,
      onSelect: (value) => { state.type = value; writeParam(TYPE_PARAM, value); apply(); }
    });
    const nextSort = buildControl({
      key: 'sort',
      label: 'Sort by',
      options: SORT_OPTIONS,
      selected: state.sort,
      onSelect: (value) => { state.sort = value; writeParam(SORT_PARAM, value === 'manual' ? '' : value); apply(); }
    });

    if (typeControl) typeControl.replaceWith(nextType); else toolbarRight.appendChild(nextType);
    if (sortControl) sortControl.replaceWith(nextSort); else toolbarRight.appendChild(nextSort);
    typeControl = nextType;
    sortControl = nextSort;

    // Only meaningful with more than one type in the vehicle's results.
    typeControl.hidden = counts.size < 2;

    syncMobileDrawer(typeOptions);

    if (!gridSwitcher.parentNode) {
      toolbarRight.appendChild(gridSwitcher);
      toolbarRight.appendChild(mobileGridSwitcher);
    }
  };

  /* ---- filter + sort over the YMM result set --------------------------- */
  const apply = () => {
    const filtered = state.type
      ? products.filter((product) => product.type === state.type)
      : products.slice();

    const sorted = filtered.slice();
    switch (state.sort) {
      case 'title-ascending': sorted.sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'title-descending': sorted.sort((a, b) => b.title.localeCompare(a.title)); break;
      case 'price-ascending': sorted.sort((a, b) => a.price - b.price); break;
      case 'price-descending': sorted.sort((a, b) => b.price - a.price); break;
      case 'created-ascending': sorted.sort((a, b) => a.created - b.created); break;
      case 'created-descending': sorted.sort((a, b) => b.created - a.created); break;
      default: sorted.sort((a, b) => a.order - b.order);
    }

    grid.textContent = '';
    tableBody.textContent = '';
    sorted.forEach((product, index) => {
      if (product.card) grid.appendChild(product.card);
      if (product.row) {
        // # column reflects position in the current filter/sort, not the
        // placeholder the row was rendered with.
        const cell = product.row.querySelector('.jci-table-cell--index');
        if (cell) cell.textContent = String(index + 1).padStart(3, '0');
        tableBody.appendChild(product.row);
      }
    });
    grid.hidden = false;

    if (countEl) {
      const b = countEl.querySelector('b');
      if (b) b.textContent = String(sorted.length);
    }

    renderControls();
    // Theme's own re-init hook - gives the new cards jci-product-listing.js's
    // media switching/swipe/hover and the grid switcher wiring.
    document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: {}, bubbles: false }));
  };

  /* ---- ingest the YMM result set --------------------------------------- */
  const parseItem = (item, index) => {
    const link = item.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    const created = Date.parse(item.getAttribute('data-prd-created-at') || '') || 0;
    return { handle: match[1], order: index, created, title: '', price: 0, type: '', card: null, row: null };
  };

  /* One request returns the grid card, the list row AND the title/type/
     price the sorting below needs (see templates/product.jci-card.liquid),
     so a vehicle result costs exactly one request per product and
     switching views refetches nothing. The row has to be parsed inside a
     real <table> - a bare <tr> is discarded by the HTML parser anywhere
     else. */
  const fetchProduct = async (product) => {
    try {
      const response = await fetch(`/products/${product.handle}?view=jci-card`);
      if (!response.ok) return;
      const html = await response.text();

      const dataJson = (html.match(/<!--JCI-DATA-->([\s\S]*?)<!--\/JCI-DATA-->/) || [])[1];
      const cardHtml = (html.match(/<!--JCI-CARD-->([\s\S]*?)<!--\/JCI-CARD-->/) || [])[1];
      const rowHtml = (html.match(/<!--JCI-ROW-->([\s\S]*?)<!--\/JCI-ROW-->/) || [])[1];

      if (dataJson) {
        // Leave the defaults on a parse failure; the card still renders,
        // it just sorts as 0/"".
        try {
          const data = JSON.parse(dataJson);
          product.title = data.title || '';
          product.type = (data.type || '').trim();
          product.price = typeof data.price === 'number' ? data.price : 0;
        } catch (error) {
          /* keep defaults */
        }
      }

      if (cardHtml) {
        const template = document.createElement('template');
        template.innerHTML = cardHtml.trim();
        product.card = template.content.querySelector('.jci-product-card') || template.content.firstElementChild;
      }
      if (rowHtml) {
        const table = document.createElement('table');
        table.innerHTML = `<tbody>${rowHtml.trim()}</tbody>`;
        product.row = table.querySelector('tr');
      }
    } catch (error) {
      // Fail soft: this product just won't render rather than breaking the page.
    }
  };

  let lastKey = '';
  let running = false;
  let ingestPending = false;
  let ingestTimer = 0;

  const readItems = () => Array.from(appGrid.querySelectorAll('.ymm-grid-item'));

  // The YMM app can append a result set in more than one DOM batch. Build
  // the identity from product links (rather than optional element IDs) so
  // a later batch is always detected before the app markup is replaced.
  const itemsKey = (items) => items.map((item) => {
    const link = item.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href') : item.id;
  }).join('|');

  const ingest = async () => {
    if (running) {
      ingestPending = true;
      return;
    }

    const items = readItems();
    if (!items.length) return;

    const key = itemsKey(items);
    if (key === lastKey) return;
    lastKey = key;
    running = true;
    ingestPending = false;

    products = items.map(parseItem).filter(Boolean);
    syncCount();

    // One tiny layout-less request per product; they run together so the
    // whole set resolves in roughly one round trip.
    await Promise.all(products.map(fetchProduct));

    running = false;

    // Do not present a partial YMM result set. If the app appended or
    // replaced cards while the Liquid card requests were in flight, ingest
    // the complete, newer source set instead.
    const latestItems = readItems();
    if (ingestPending || (latestItems.length && itemsKey(latestItems) !== lastKey)) {
      ingestPending = false;
      scheduleIngest();
      return;
    }

    apply();
  };

  // Debounce until the app has stopped appending result batches. The
  // presentation grid is separate, so rendering it cannot retrigger this
  // observer or interfere with the app's own #products updates.
  const scheduleIngest = () => {
    window.clearTimeout(ingestTimer);
    ingestTimer = window.setTimeout(ingest, 250);
  };

  scheduleIngest();
  new MutationObserver(scheduleIngest).observe(appGrid, { childList: true });
})();
